import React from "react";

import moment from "moment";

import { TFunction, trimToDecimals } from "utils";

import {
  CMC_PH_TYPE,
  CMC_SOIL_TYPE,
  CROP_ROTATION,
  CROP_TYPE_FCPO,
  CropFCPO,
  CropFormValidationError,
  CropMixComposerCrop,
  CropMixComposerGoalResponse,
  CropMixComposerSettings,
  CropPracticesRequestBody,
  CropPracticesResponse,
  CropsOverviewTableColumn,
  FieldPracticeResponse,
  GOAL_LEVEL_2,
  GoalScore,
  MainCropSettingsCMC,
  MixDistributionShare,
  PracticeCrop,
  SEED_COST_RATE,
  ScoreFactors,
  SortState,
  WithInjectedName,
} from "models";

import { Flex, Icon, Message } from "components";

import { CMC_ONBOARDING_1_ERRORS } from "pages/app/crop-mix-composer/CropMixComposerOverlay";
import { CMCMainCrops } from "pages/app/field-timeline/utils/TimelineUtils";

// New unified type for goal score calculation
export type UnifiedGoalScoreItem = {
  goalScores: Array<{ level2: GOAL_LEVEL_2; score: number }>;
  weightShare: number;
  cropFamily: string;
};

/**
 * Unified goal score calculation that works for both commercial mixes and crop mix composer
 * Returns GoalScore[] instead of GoalToScoreMap for consistency
 */
export const calculateUnifiedGoalScores = (items: UnifiedGoalScoreItem[], selectedGoals: GOAL_LEVEL_2[], scoreFactors: ScoreFactors): GoalScore[] => {
  const cropsCount = items.length;
  if (!cropsCount || !selectedGoals.length) {
    return [];
  }

  // Calculate score factors based on number of crops and plant families
  const plantFamiliesCount = new Set(items.map((item) => item.cropFamily)).size;
  const cropsScoreFactor = cropsCount > 20 ? 1 : scoreFactors?.crops?.find((factor) => factor.count === cropsCount)?.score ?? 0;
  const plantFamiliesScoreFactor = plantFamiliesCount > 10 ? 1 : scoreFactors?.plantFamilies?.find((factor) => factor.count === plantFamiliesCount)?.score ?? 0;

  // Calculate weighted goal scores
  return selectedGoals.map((goal) => {
    const totalWeightedScore = items.reduce((total, item) => {
      // Find the score for this specific goal from the item's goal scores
      const goalScore = item.goalScores?.find((score) => score.level2 === goal)?.score ?? 0;

      // Handle NaN values by treating them as 0
      const safeGoalScore = Number.isNaN(goalScore) ? 0 : goalScore;
      const safeWeightShare = Number.isNaN(item.weightShare) ? 0 : item.weightShare;

      // Weight the score by the item's proportion in the mix
      const weightedScore = (safeGoalScore * safeWeightShare) / 100;

      return total + weightedScore;
    }, 0);

    // Apply score factors and return the rounded result
    const finalScore = totalWeightedScore * cropsScoreFactor * plantFamiliesScoreFactor;

    return {
      name: goal,
      score: Math.round(finalScore),
    };
  });
};

/**
 * Convenience function for using unified goal score calculation with CropMixComposerGoalResponse data
 * This can replace generateGoalsToScoresMap when you want GoalScore[] instead of GoalToScoreMap
 */
export const generateGoalScores = (
  currentGoals: GOAL_LEVEL_2[],
  mixDistributionShares: MixDistributionShare,
  scoreFactors: ScoreFactors,
  selectedCrops: CropMixComposerGoalResponse[]
): GoalScore[] => {
  // Transform CropMixComposerGoalResponse to UnifiedGoalScoreItem
  const unifiedItems: UnifiedGoalScoreItem[] = selectedCrops.map((crop) => ({
    goalScores:
      crop.scores?.map((score) => ({
        level2: score.level2,
        score: score.score,
      })) ?? [],
    weightShare: mixDistributionShares[crop.crop.id]?.weightShare ?? 0,
    cropFamily: crop.crop.cropFamily,
  }));

  return calculateUnifiedGoalScores(unifiedItems, currentGoals, scoreFactors);
};

export const calculateAggregateMatchScore = (selectedCrops: CropMixComposerGoalResponse[], mixDistributionShares: MixDistributionShare) => {
  if (!selectedCrops.length) return 0;

  return Math.round(selectedCrops.reduce((sum, crop) => sum + (crop.matchScore * (mixDistributionShares[crop.crop.id].weightShare ?? 0)) / 100, 0));
};

export const calculateMixDistributionSharesForAutoGeneratedMix = (cropsWithGoals: CropMixComposerGoalResponse[]): MixDistributionShare => {
  if (!cropsWithGoals.length) return {};

  const mixDistributionShares = cropsWithGoals.reduce((acc, cropWithGoals) => {
    acc[cropWithGoals.crop.id] = {
      name: cropWithGoals.crop.name,
      weightShare: cropWithGoals.autoMixWeightShare,
      weightSeedingRate: trimToDecimals((cropWithGoals.weightDistributionParams.seedingRateKgHa * cropWithGoals.autoMixWeightShare) / 100),
      minShareMix: cropWithGoals.weightDistributionParams.minShareMix,
      maxShareMix: cropWithGoals.weightDistributionParams.maxShareMix,
      minimumGrowTimeWeeks: cropWithGoals.weightDistributionParams.minimumGrowTimeWeeks,
    };
    return acc;
  }, {} as MixDistributionShare);

  return mixDistributionShares;
};

export const calculateMixDistributionShares = (cropsWithGoals: CropMixComposerGoalResponse[], cropsCMC?: CropMixComposerCrop[]): MixDistributionShare => {
  const shouldCalculateWeightShares =
    !cropsCMC ||
    cropsCMC.length === 0 ||
    cropsCMC.length !== cropsWithGoals.length ||
    !cropsWithGoals.every((crop) => cropsCMC.some((c) => c.cropId === crop.crop.id));

  const evenShare = 100 / cropsWithGoals.length;

  const mixDistributionShares = cropsWithGoals.reduce((acc, cropWithGoals) => {
    const savedWeightShare = cropsCMC?.find((c) => c.cropId === cropWithGoals.crop.id)?.weightShare;
    const weightShare = shouldCalculateWeightShares || !savedWeightShare ? trimToDecimals(evenShare) : savedWeightShare;

    acc[cropWithGoals.crop.id] = {
      name: cropWithGoals.crop.name,
      weightShare: weightShare,
      weightSeedingRate: trimToDecimals((cropWithGoals.weightDistributionParams.seedingRateKgHa * weightShare) / 100),
      minShareMix: cropWithGoals.weightDistributionParams.minShareMix,
      maxShareMix: cropWithGoals.weightDistributionParams.maxShareMix,
      minimumGrowTimeWeeks: cropWithGoals.weightDistributionParams.minimumGrowTimeWeeks,
    };
    return acc;
  }, {} as MixDistributionShare);

  // if the weight shares were saved in the db, return the existing shares
  if (!shouldCalculateWeightShares) return mixDistributionShares;

  const applyMinMaxConstraints = (share: number, minShare: number, maxShare: number): number => {
    if (share < minShare) {
      return minShare;
    }
    if (share > maxShare) {
      return maxShare;
    }
    return share;
  };

  const unchangedComponents: MixDistributionShare = {};
  // Apply min and max constraints to the weight share
  Object.entries(mixDistributionShares).forEach(([cropId, share]) => {
    const { weightShare, weightSeedingRate, minShareMix, maxShareMix } = share;
    const newShare = applyMinMaxConstraints(weightShare, minShareMix, maxShareMix);
    if (newShare !== weightShare) {
      mixDistributionShares[cropId] = {
        ...share,
        weightShare: trimToDecimals(newShare),
        weightSeedingRate: trimToDecimals((weightSeedingRate * newShare) / 100),
      };
    } else {
      unchangedComponents[cropId] = { ...share };
    }
  });

  let attempts = 0;
  let totalWeightShare = Math.round(Object.values(mixDistributionShares).reduce((sum, result) => sum + result.weightShare, 0));

  while (totalWeightShare !== 100 && attempts < 10) {
    const exceedingShare = 100 - totalWeightShare;
    const componentsToAdjust = exceedingShare > 0 ? mixDistributionShares : unchangedComponents;

    if (Object.keys(componentsToAdjust).length > 0) {
      const totalAdjustableWeight = Object.values(componentsToAdjust).reduce((sum, share) => sum + share.weightShare, 0);

      Object.entries(componentsToAdjust).forEach(([cropId, share]) => {
        const proportionalAdjustment = (share.weightShare * exceedingShare) / totalAdjustableWeight;
        const newWeightShare = share.weightShare + proportionalAdjustment;

        const crop = cropsWithGoals.find((c) => c.crop.id === cropId);
        const seedingRateKgHa = crop?.weightDistributionParams.seedingRateKgHa || 0;
        const newWeightSeedingRate = seedingRateKgHa * newWeightShare;

        mixDistributionShares[cropId] = {
          ...share,
          weightShare: trimToDecimals(newWeightShare),
          weightSeedingRate: trimToDecimals(newWeightSeedingRate / 100),
        };
      });

      totalWeightShare = Math.round(Object.values(mixDistributionShares).reduce((sum, result) => sum + result.weightShare, 0));
    }

    attempts += 1;
  }

  // Adjust the weight shares to match the total seeding rate
  const totalSeedingRate = Object.values(mixDistributionShares).reduce((sum, result) => sum + result.weightSeedingRate, 0);

  Object.entries(mixDistributionShares).forEach(([cropId, share]) => {
    mixDistributionShares[cropId] = { ...share, weightShare: trimToDecimals((share.weightSeedingRate / totalSeedingRate) * 100) };
  });

  return mixDistributionShares;
};

export const getCostSymbol = (coastCategory: SEED_COST_RATE) => {
  switch (coastCategory) {
    case SEED_COST_RATE.HIGH:
      return (
        <Flex>
          <Icon size="s" iconName="tabler/currency-euro" />
          <Icon size="s" iconName="tabler/currency-euro" />
          <Icon size="s" iconName="tabler/currency-euro" />
        </Flex>
      );
    case SEED_COST_RATE.MEDIUM:
      return (
        <Flex>
          <Icon size="s" iconName="tabler/currency-euro" />
          <Icon size="s" iconName="tabler/currency-euro" />
        </Flex>
      );
    case SEED_COST_RATE.LOW:
      return <Icon size="s" iconName="tabler/currency-euro" />;
    default:
      return null;
  }
};

export const getAvgCostSymbols = (selectedCrops: CropMixComposerGoalResponse[]) => {
  const totalCrops = selectedCrops.length;
  const avgCost = Math.trunc(
    selectedCrops.reduce((sum, crop) => {
      const costValue = crop.crop.coastCategory === SEED_COST_RATE.HIGH ? 3 : crop.crop.coastCategory === SEED_COST_RATE.MEDIUM ? 2 : 1;
      return sum + costValue;
    }, 0) / totalCrops
  );
  return avgCost === 3 ? getCostSymbol(SEED_COST_RATE.HIGH) : avgCost === 2 ? getCostSymbol(SEED_COST_RATE.MEDIUM) : getCostSymbol(SEED_COST_RATE.LOW);
};

export const tableSorting = (
  a: CropMixComposerGoalResponse,
  b: CropMixComposerGoalResponse,
  sortState: SortState,
  t: (key: string) => string,
  selectedGoals: GOAL_LEVEL_2[]
) => {
  if (!sortState.column || !sortState.direction) return 0;
  const [elementA, elementB] = sortState.direction === "asc" ? [a, b] : [b, a];

  switch (sortState.column) {
    case CropsOverviewTableColumn.COVER_CROP:
      return elementA.crop.name.localeCompare(elementB.crop.name);
    case CropsOverviewTableColumn.LOCATION_MATCH: {
      const scoreA = elementA.matchScore ?? 0;
      const scoreB = elementB.matchScore ?? 0;
      return scoreA - scoreB;
    }
    default: {
      const goalIndex = selectedGoals.indexOf(sortState.column as GOAL_LEVEL_2);
      if (goalIndex !== -1) {
        const goalScoreA = elementA.scores.find((score) => score.level2 === selectedGoals[goalIndex])?.score ?? 0;
        const goalScoreB = elementB.scores.find((score) => score.level2 === selectedGoals[goalIndex])?.score ?? 0;
        return goalScoreA - goalScoreB;
      }
      return 0;
    }
  }
};

export const addMainCropToRotation = async (
  t: TFunction,
  mainCropsBeforeAndAfterRecommendation: CMCMainCrops,
  nextFutureCrop: CropFCPO | undefined,
  mainCropSettings: MainCropSettingsCMC | undefined,
  crops: WithInjectedName<PracticeCrop>[],
  practicesResponse: FieldPracticeResponse | undefined,
  fieldId: string,
  signal: AbortSignal,
  validatePeriods: (crop: CropFCPO, crops: CropFCPO[]) => CropFormValidationError[],
  addFieldCropPractice: (data: { fieldId: string; requestBody: CropPracticesRequestBody; signal: AbortSignal }) => Promise<CropPracticesResponse>
) => {
  if (nextFutureCrop && moment(nextFutureCrop?.seedingStartDate).diff(mainCropSettings?.seedingDate, "months") < 1) {
    Message({ content: t("errors.secondMainCropLessThanOneMonth"), icon: "error" });
    return;
  }

  // set harvest date to beginning of the following year
  let harvestDate = moment(mainCropSettings?.seedingDate)
    .add(1, "year")
    .startOf("year")
    .add(1, "day")
    .toISOString();

  // if growing period < 1 month, set harvest date to 2 months from seeding date
  if (moment(harvestDate).diff(mainCropSettings?.seedingDate, "months") < 1) {
    harvestDate = moment(mainCropSettings?.seedingDate)
      .add(4, "month")
      .toISOString();
  }

  // if there is no main crop set harvest date to regional value
  if (!mainCropsBeforeAndAfterRecommendation.after?.cropId) {
    const regionalHarvestDayAndMonth = crops?.find((crop) => crop.id === mainCropSettings?.crop2Id)?.sequestrationValues?.[0]?.harvestStart;
    let regionalHarvestDate = moment(`${regionalHarvestDayAndMonth}/${moment(mainCropSettings?.seedingDate).year()}`, "DD/MM/YYYY");
    if (regionalHarvestDate.isBefore(mainCropSettings?.seedingDate)) {
      regionalHarvestDate = moment(`${regionalHarvestDayAndMonth}/${moment(mainCropSettings?.seedingDate).year() + 1}`, "DD/MM/YYYY");
    }
    const isRegionalHarvestDateValid =
      !!regionalHarvestDayAndMonth && !!regionalHarvestDate && !moment(regionalHarvestDate).isBefore(mainCropSettings?.seedingDate);
    harvestDate = isRegionalHarvestDateValid ? regionalHarvestDate.toISOString() : harvestDate;
  }

  // if the next future crop is the main crop of the following year, set harvest date to 1 day before seeding date of the next main crop
  if (
    moment(nextFutureCrop?.seedingStartDate).year() === moment(mainCropSettings?.seedingDate).year() + 1 &&
    nextFutureCrop?.year === moment(mainCropSettings?.seedingDate).year() + 2
  ) {
    harvestDate = moment(nextFutureCrop.seedingStartDate).subtract(1, "day").toISOString();
  }

  const mainCropRequest = {
    cropId: mainCropSettings?.crop2Id || "",
    cropType: CROP_TYPE_FCPO.MAIN,
    harvestStartDate: null,
    harvestEndDate: harvestDate,
    seedingStartDate: mainCropSettings?.seedingDate || "",
    seedingEndDate: null,
    seedingPracticeType: null,
    seedingVerificationStatus: null,
    hasResidue: null,
  };

  const periodValidationErrors = validatePeriods(
    {
      ...(mainCropRequest as CropFCPO),
      id: "",
      cropName: "",
      isConfirmed: false,
      payout: 0,
      translationSlug: "",
    },
    practicesResponse?.crops || []
  );

  if (periodValidationErrors.length > 0) {
    Message({ content: t("errors.mainCropOverlap"), icon: "error" });
    return;
  }

  const mainCropRequestBody: CropPracticesRequestBody = {
    crop: mainCropRequest,
    practices: [],
    hasFertilizerManagement: null,
  };

  await addFieldCropPractice({
    requestBody: mainCropRequestBody,
    fieldId: fieldId || "",
    signal: signal,
  });
};

export const validateOnboarding1 = (
  mainCropSettings: MainCropSettingsCMC | undefined,
  soilType: CMC_SOIL_TYPE | undefined | null,
  soilPh: CMC_PH_TYPE | undefined | null,
  drought: boolean | undefined | null,
  cropRotationFamiliesToExclude: CROP_ROTATION[],
  legumeExclusionConfirmation: boolean | null
): { errors: CMC_ONBOARDING_1_ERRORS[]; isValid: boolean } => {
  const errors: CMC_ONBOARDING_1_ERRORS[] = [];

  if (!soilType) {
    errors.push(CMC_ONBOARDING_1_ERRORS.SOIL_TYPE);
  }

  if (!soilPh) {
    errors.push(CMC_ONBOARDING_1_ERRORS.SOIL_PH);
  }

  if (typeof drought !== "boolean") {
    errors.push(CMC_ONBOARDING_1_ERRORS.DROUGHT);
  }

  if (!mainCropSettings?.crop1Id || !mainCropSettings?.harvestDate) {
    errors.push(CMC_ONBOARDING_1_ERRORS.MAIN_CROP_1);
  }

  if (!mainCropSettings?.crop2Id || !mainCropSettings?.seedingDate) {
    errors.push(CMC_ONBOARDING_1_ERRORS.MAIN_CROP_2);
  }

  if (!errors.includes(CMC_ONBOARDING_1_ERRORS.MAIN_CROP_1) && !errors.includes(CMC_ONBOARDING_1_ERRORS.MAIN_CROP_2)) {
    const harvestDate = moment(mainCropSettings?.harvestDate);
    const seedingDate = moment(mainCropSettings?.seedingDate);

    if (seedingDate.isSameOrBefore(harvestDate)) {
      errors.push(CMC_ONBOARDING_1_ERRORS.SEEDING_BEFORE_HARVEST);
    }

    if (seedingDate.diff(harvestDate, "weeks") < 4) {
      errors.push(CMC_ONBOARDING_1_ERRORS.MINIMUM_GROWING_PERIOD);
    }
  }

  if (cropRotationFamiliesToExclude.includes(CROP_ROTATION.FABACEAE) && legumeExclusionConfirmation === null) {
    errors.push(CMC_ONBOARDING_1_ERRORS.LEGUME_EXCLUSION_CONFIRMATION);
  }

  return { errors, isValid: errors.length === 0 };
};

export const getNextAvailableMixName = (mixNamePrefix: string, settingsCMC: CropMixComposerSettings[] | undefined): string => {
  if (!settingsCMC || settingsCMC.length === 0) {
    return `${mixNamePrefix} 01`;
  }

  const existingNumbers = new Set<number>();

  // Extract numbers from existing mix names
  settingsCMC.forEach((mix) => {
    if (mix.name.startsWith(mixNamePrefix)) {
      const numberMatch = mix.name.match(new RegExp(`${mixNamePrefix}\\s*(\\d+)`));
      if (numberMatch) {
        const number = parseInt(numberMatch[1], 10);
        if (!Number.isNaN(number)) {
          existingNumbers.add(number);
        }
      }
    }
  });

  // Find the next available number
  let nextNumber = 1;
  while (existingNumbers.has(nextNumber)) {
    nextNumber += 1;
  }

  return `${mixNamePrefix} ${String(nextNumber).padStart(2, "0")}`;
};
